import { useEffect, useMemo, useState } from "react";
import { fetchRoles, listCareerPathOverrides, upsertCareerPathOverride, deleteCareerPathOverride } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { toast } from "sonner";
import { ArrowRight, Star, Ban, Trash2, Save, Route, Loader2 } from "lucide-react";

/**
 * CareerPathEditor — HR curates role→role transitions.
 * "featured" = explicitly recommended path (shown highlighted in Career Tree)
 * "blocked"  = not allowed (hidden from suggestions)
 */
export default function CareerPathEditor() {
  const [roles, setRoles] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [fromRole, setFromRole] = useState("");
  const [toRole, setToRole] = useState("");
  const [kind, setKind] = useState("featured");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = () => listCareerPathOverrides().then((d) => setOverrides(d.items || []));

  useEffect(() => {
    fetchRoles().then(setRoles);
    reload();
  }, []);

  const rolesById = useMemo(() => Object.fromEntries(roles.map((r) => [r.id, r])), [roles]);

  const handleSave = async () => {
    if (!fromRole || !toRole) {
      toast.error("Kaynak ve hedef rolü seçin");
      return;
    }
    if (fromRole === toRole) {
      toast.error("Aynı rol seçilemez");
      return;
    }
    setSaving(true);
    try {
      await upsertCareerPathOverride({ from_role_id: fromRole, to_role_id: toRole, kind, note });
      toast.success(kind === "featured" ? "Öne çıkan yol eklendi ⭐" : "Yol engellendi 🚫");
      setNote("");
      reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kaydedilemedi";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCareerPathOverride(id);
      toast.success("Silindi");
      reload();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const featured = overrides.filter((o) => o.kind === "featured");
  const blocked = overrides.filter((o) => o.kind === "blocked");

  return (
    <div className="space-y-5" data-testid="career-path-editor">
      <div className="card-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <Route className="w-4 h-4 text-indigo-600" />
          <div>
            <div className="label-eyebrow">Kariyer Yolu Editörü (YTN-32)</div>
            <h3 className="font-display text-lg font-semibold">Rol → Rol Geçişleri</h3>
            <p className="text-xs text-slate-500 mt-0.5">Belirli geçişleri &ldquo;öne çıkan yol&rdquo; olarak işaretle veya &ldquo;engelle&rdquo; — çalışanların Kariyer Ağacı bu tanıma göre şekillenir.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Kaynak Rol</label>
            <Select value={fromRole} onValueChange={setFromRole}>
              <SelectTrigger className="h-9 bg-white" data-testid="path-from-role"><SelectValue placeholder="Rol seç..." /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.title} ({r.career_band})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Hedef Rol</label>
            <Select value={toRole} onValueChange={setToRole}>
              <SelectTrigger className="h-9 bg-white" data-testid="path-to-role"><SelectValue placeholder="Rol seç..." /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {roles.filter((r) => r.id !== fromRole).map((r) => <SelectItem key={r.id} value={r.id}>{r.title} ({r.career_band})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Tür</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9 bg-white" data-testid="path-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="featured">⭐ Öne Çıkan Yol</SelectItem>
                <SelectItem value="blocked">🚫 Engelli Yol</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Not (opsiyonel)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn: dijital dönüşüm önceliği" className="h-9" data-testid="path-note" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="sm" data-testid="path-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Yolu Kaydet
          </Button>
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PathList title="Öne Çıkan Yollar" icon={Star} accent="amber" items={featured} rolesById={rolesById} onDelete={handleDelete} emptyMsg="Öne çıkan yol henüz tanımlanmadı." testid="featured-paths-list" />
        <PathList title="Engelli Yollar" icon={Ban} accent="rose" items={blocked} rolesById={rolesById} onDelete={handleDelete} emptyMsg="Engellenmiş yol yok." testid="blocked-paths-list" />
      </div>
    </div>
  );
}

function PathList({ title, icon: Icon, accent, items, rolesById, onDelete, emptyMsg, testid }) {
  const cmap = { amber: "text-amber-700 bg-amber-50 border-amber-200", rose: "text-rose-700 bg-rose-50 border-rose-200" };
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-3 text-sm font-semibold ${accent === "amber" ? "text-amber-700" : "text-rose-700"}`}>
        <Icon className="w-4 h-4" />
        {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic">{emptyMsg}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cmap[accent]}`}>
              <div className="flex-1 min-w-0 text-sm">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium truncate">{it.from_role_title || rolesById[it.from_role_id]?.title || it.from_role_id}</span>
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-70" />
                  <span className="font-medium truncate">{it.to_role_title || rolesById[it.to_role_id]?.title || it.to_role_id}</span>
                </div>
                {it.note && <div className="text-[11px] text-slate-500 mt-0.5">{it.note}</div>}
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDelete(it.id)} data-testid={`path-delete-${it.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-slate-400" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
